import type { useIdentificationSteps } from '@/account/kyc/useIdentificationSteps';
import type { useParsedAccessToken } from '@/auth/api';
import type { AccountUser, WealthMarketplaceMerchant } from '@/backend';
import { IS_PRODUCTION, WORKSPACE } from '@/base/constants';
import { devFeatureOverrides } from '@/dev-tools/featureOverrides';
import type { useRuntimeEnv } from '@/shared/runtimeEnv';

export type GateHook = (context: {
  hasAccessToken: boolean;
  parsedAccessToken: ReturnType<typeof useParsedAccessToken> | null;
  user: AccountUser | null;
  merchant: WealthMarketplaceMerchant | null;
  runtimeEnv: ReturnType<typeof useRuntimeEnv>;
  identificationSteps: ReturnType<typeof useIdentificationSteps>;
  location: string;
}) => boolean;

export const isAdminGateHook: GateHook = ({
  hasAccessToken,
  parsedAccessToken,
}) => {
  return hasAccessToken && !!parsedAccessToken?.admin;
};

export const isSuperAdminGateHook: GateHook = ({
  hasAccessToken,
  parsedAccessToken,
}) => {
  return hasAccessToken && !!parsedAccessToken?.admin?.is_superadmin;
};

export type AdminRole = 'kyc' | 'withdrawal' | 'treasury';

export const hasAdminRoleGateHook =
  (role: AdminRole): GateHook =>
  ({ hasAccessToken, parsedAccessToken }) => {
    if (!hasAccessToken || !parsedAccessToken?.admin) return false;
    return (
      parsedAccessToken.admin.is_superadmin ||
      parsedAccessToken.admin.roles?.includes(role)
    );
  };

export const isStandaloneGateHook =
  (value: boolean): GateHook =>
  ({ runtimeEnv }) => {
    return runtimeEnv.isStandalone === value;
  };

export const hasAccessTokenGateHook: GateHook = ({ hasAccessToken }) => {
  return hasAccessToken;
};

export const embeddedAppRequiresAuthHook: GateHook = ({
  hasAccessToken,
  runtimeEnv,
}) => {
  if (!runtimeEnv.isStandalone && !runtimeEnv.isTelegramMiniApp) return true;
  if (WORKSPACE === 'public') return true;
  return hasAccessToken;
};

export const hasFeatureGateHook =
  (feature: string): GateHook =>
  ({ user }) => {
    // Dev-tools overrides win over everything, including the
    // non-production passthrough, so removals are testable in dev.
    if (devFeatureOverrides.removed.includes(feature)) return false;
    if (devFeatureOverrides.added.includes(feature)) return true;
    if (!IS_PRODUCTION) return true;
    const features = user?.features ?? [];
    if (features.includes('//all')) return true;
    return features.includes(feature);
  };

export const isIdentified =
  (minStep: number): GateHook =>
  ({ identificationSteps, hasAccessToken }) => {
    return (
      hasAccessToken &&
      identificationSteps
        .slice(0, minStep)
        .every((x) => x.status === 'completed')
    );
  };

export const isMerchant =
  (onlyIfApproved?: boolean): GateHook =>
  ({ hasAccessToken, merchant }) => {
    return (
      hasAccessToken &&
      !!merchant &&
      (onlyIfApproved ? merchant.status === 'APPROVED' : true)
    );
  };
